import { createTheme, darken, lighten, PaletteColor, PaletteColorOptions, Theme } from '@mui/material/styles'
import '@mui/x-date-pickers/themeAugmentation'

// Import geologica font weights from @fontsource
import '@fontsource/geologica/300.css' // Light weight
import '@fontsource/geologica/400.css' // Regular weight
import '@fontsource/geologica/500.css' // Medium weight
import '@fontsource/raleway/700.css' // Bold weight

declare module '@mui/material/styles' {
  interface TypeText {
    greyOut: string
    darkBackground: string
    contrastText: string
  }
  interface Palette {
    custom: {
      linkLight: PaletteColor
      linkDark: PaletteColor
    }
  }
  interface TypeBackground {
    default: string
    light: string
  }
  interface PaletteOptions {
    custom?: {
      linkLight: PaletteColorOptions & { main: string; light: string; dark: string; contrastText: string }
      linkDark: PaletteColorOptions & { main: string; light: string; dark: string; contrastText: string }
    }
  }
}

/* ------------------------ Colors ------------------------ */
const primary = '#23A9C2'
const secondary = '#E1BF92'
const secondaryLight = '#F7E7D3'
const success = '#4caf50'
const error = '#C12C2C'
const contrastText = '#ffffff'
const background = '#FFFFFF'
const linkLight = '#FFFFFF'
const linkDark = '#000000'
const text = '#000000'
const textDarkBackground = '#303030'
const greyOut = '#A0A0A0'
const lightBackground = '#F3F3F3'

//----- Border Radius -----
export const INPUT_BORDER_RADIUS = '50px'
export const MULTILINE_TEXT_MOBILE_BORDER_RADIUS = '25px'
const DATE_PICKER_BORDER_READIUS = '15px'
const breakpointValues = {
  mobile: 0,
  tablet: 600,
  laptop: 960,
  desktop: 1280,
  largeDesktop: 2000,
  xl: 3000,
}
const tabletUp = `@media (min-width:${breakpointValues.tablet}px)`
const laptopUp = `@media (min-width:${breakpointValues.laptop}px)`
const desktopUp = `@media (min-width:${breakpointValues.desktop}px)`

export const theme = createTheme({
  /*--------------------- Palette ---------------------*/
  palette: {
    primary: {
      main: primary,
      light: lighten(primary, 0.2),
      dark: darken(primary, 0.2),
      contrastText,
    },
    secondary: {
      main: secondary,
      light: secondaryLight,
      dark: darken(secondary, 0.2),
      contrastText,
    },
    success: {
      main: success,
      contrastText,
    },
    error: {
      main: error,
      contrastText,
    },
    background: {
      default: background,
      light: lightBackground,
    },
    text: {
      primary: text,
      secondary: text,
      greyOut,
      darkBackground: textDarkBackground,
      contrastText,
    },
    custom: {
      linkLight: {
        main: linkLight,
        light: lighten(primary, 0.2),
        dark: darken(primary, 0.2),
        contrastText,
      },
      linkDark: {
        main: linkDark,
        light: lighten(linkDark, 0.1),
        dark: darken(linkDark, 0.1),
        contrastText,
      },
    },
  },
  /*--------------------- Typography ---------------------*/
  typography: {
    fontSize: 21,
    fontFamily: '"Geologica", Arial, sans-serif',
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    h1: {
      fontSize: '2.5rem',
      [tabletUp]: { fontSize: '3rem' },
      [laptopUp]: { fontSize: '3.5rem' },
      [desktopUp]: { fontSize: '4rem' },
    },
    h2: {
      fontSize: '2rem',
      [tabletUp]: { fontSize: '2.5rem' },
      [laptopUp]: { fontSize: '3rem' },
      [desktopUp]: { fontSize: '3.5rem' },
    },
    h3: {
      fontSize: '1.75rem',
      [tabletUp]: { fontSize: '2rem' },
      [laptopUp]: { fontSize: '2.5rem' },
      [desktopUp]: { fontSize: '3rem' },
    },
    h4: {
      fontSize: '1.5rem',
      [tabletUp]: { fontSize: '1.7rem' },
      [laptopUp]: { fontSize: '1.75rem' },
      [desktopUp]: { fontSize: '2rem' },
    },
    h5: { fontWeight: 600 },
    button: { textTransform: 'none' },
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
  },
  /*--------------------- Breakpoints ---------------------*/
  breakpoints: {
    values: breakpointValues,
  },
  /*--------------------- Components Overrides ---------------------*/
  components: {
    // --------------------- Button ---------------------
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: INPUT_BORDER_RADIUS,
          fontSize: 20,
          fontWeight: 400,
          padding: '8px 30px',
          borderWidth: 2,
        },
      },
    },
    // --------------------- InputLabel ---------------------
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          fontSize: 18,
          lineHeight: '24px',
          left: 15,
          [theme.breakpoints.down('tablet')]: { fontSize: 15, lineHeight: '33px' },
        }),
        shrink: {
          left: 1,
        },
      },
    },
    // --------------------- OutlinedInput ---------------------
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderRadius: INPUT_BORDER_RADIUS,
          backgroundColor: theme.palette.background.light,
          fontSize: 18,
          lineHeight: '24px',
          '&.MuiInputBase-multiline': {
            borderRadius: MULTILINE_TEXT_MOBILE_BORDER_RADIUS,
          },
          '& .MuiSelect-select': {
            height: '1em !important',
          },
          [theme.breakpoints.down('tablet')]: { fontSize: 15, lineHeight: '33px' },
        }),
        input: {
          padding: '15px 30px',
        },
      },
    },
    // --------------------- TableCell ---------------------
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          fontSize: 18,
          [theme.breakpoints.down('tablet')]: { fontSize: 15 },
        }),
      },
    },
    // --------------------- FormControlLabel ---------------------
    MuiFormControlLabel: {
      styleOverrides: {
        label: ({ theme }: { theme: Theme }) => ({
          fontSize: 18,
          [theme.breakpoints.down('tablet')]: { fontSize: 15 },
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          fontSize: 15,
          [theme.breakpoints.down('tablet')]: { fontSize: 12 },
        }),
      },
    },
    // --------------------- Tooltip ---------------------
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }: { theme: Theme }) => ({
          color: theme.palette.secondary.contrastText,
        }),
        popper: {
          position: 'fixed',
        },
      },
    },
    // --------------------- Alert ---------------------
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          fontSize: 18,
          [theme.breakpoints.down('tablet')]: { fontSize: 15 },
        }),
      },
    },
    // --------------------- List ---------------------
    MuiList: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          fontSize: 18,
          [theme.breakpoints.down('tablet')]: { fontSize: 15 },
        }),
      },
    },
    // --------------------- ListItemText ---------------------
    MuiListItemText: {
      styleOverrides: {
        primary: ({ theme }: { theme: Theme }) => ({
          fontSize: 18,
          [theme.breakpoints.down('tablet')]: { fontSize: 15 },
        }),
      },
    },
    // --------------------- Datepicker ---------------------
    MuiPickersLayout: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent !important',
        },
      },
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          width: 350,
          borderRadius: DATE_PICKER_BORDER_READIUS,
          backgroundColor: theme.palette.primary.contrastText,
          fontSize: 18,
        }),
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        label: ({ theme }: { theme: Theme }) => ({
          fontSize: 18,
          [theme.breakpoints.down('tablet')]: { fontSize: 15 },
        }),
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        weekContainer: ({ theme }: { theme: Theme }) => ({
          fontSize: 18,
          [theme.breakpoints.down('tablet')]: { fontSize: 15 },
        }),
      },
    },
    MuiPickersMonth: {
      styleOverrides: {
        monthButton: ({ theme }: { theme: Theme }) => ({
          fontSize: 18,
          [theme.breakpoints.down('tablet')]: { fontSize: 15 },
          '&.Mui-selected': {
            backgroundColor: `${theme.palette.primary.main} !important`,
            color: 'white',
            '&:hover': {
              backgroundColor: `${theme.palette.primary.dark} !important`,
            },
          },
        }),
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          '&.Mui-selected': {
            backgroundColor: `${theme.palette.primary.main} !important`,
            color: 'white',
            '&:hover': {
              backgroundColor: `${theme.palette.primary.dark} !important`,
            },
          },
        }),
      },
    },
    // --------------------- Dialog ---------------------
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '20px',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderBottom: `1px solid ${theme.palette.text.greyOut}`,
          padding: '20px 43px',
          fontSize: 22,
          [theme.breakpoints.down('tablet')]: { fontSize: 17 },
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          padding: '20px 43px',
          fontSize: 18,
          [theme.breakpoints.down('tablet')]: { fontSize: 15 },
        }),
      },
    },
  },
})

/*--------------------- Breakpoints ---------------------*/
declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: false // removes the `xs` breakpoint
    sm: false
    md: false
    lg: false
    xl: false
    mobile: true // adds the `mobile` breakpoint
    tablet: true
    laptop: true
    desktop: true
    largeDesktop: true
  }
}
/* ------------------------ Variables ------------------------ */
export const PAGE_TOP_PADDING = { desktop: '60px', tablet: '20px', mobile: '20px' }
export const PAGE_BOTTOM_PADDING = { desktop: '50px', tablet: '50px', mobile: '50px' }
export const FOOTER_HEIGHT = { desktop: '240px', tablet: '240px', mobile: '430px' }
export const PAGE_MAX_WIDTH = { desktop: '1480px', tablet: '100%', mobile: '100%' }
export const PAGE_WIDTH = { desktop: '90%', tablet: '90%', mobile: '90%' }
export const HEADER_HEIGHT = { desktop: '80px', tablet: '80px', mobile: '80px' }
export const HEADER_BANNER_HEIGHT = { desktop: '350px', tablet: '250px', mobile: '150px' }
export const BEACH_PLAN_HEIGHT = { desktop: '80vh', tablet: '80vh', mobile: '50vh' }
export default theme
